/**
 * Pure framing maths for the quest-guidance map card (plan 02 §2.2).
 *
 * Maps are very wide (First Field is 15000×1800, about 8:1), so the card shows
 * a window of the world-space preview around its target instead of the whole
 * strip. Everything here is in world pixels, the preview SVG's own viewBox.
 */

export interface WorldPoint { x: number; y: number }
export interface ViewWindow { x: number; y: number; width: number; height: number }

/** Card aspect (width / height) for a half-row card; two cards sit side by side. */
export const GUIDANCE_CARD_ASPECT = 3;
/** A card with the whole row to itself keeps the same height at twice the width. */
export const GUIDANCE_WIDE_CARD_ASPECT = 6;
/**
 * Narrowest window on a small map: enough ground around a single NPC to read
 * the terrain. On a wide map the narrowest window grows with the map (plan 13
 * §3: 1800 px of a 15000 px field read as a slice, not a layout).
 */
export const MIN_WINDOW_WIDTH = 1800;
/** Cap on the narrowest window, so a single NPC on a huge map stays findable. */
export const MAX_MIN_WINDOW_WIDTH = 4500;
/** Between those bounds the narrowest window is this share of the map's width. */
const MIN_WINDOW_SHARE = 0.3;
/** Smallest world margin kept left/right of the outermost target point. */
const MIN_PAD = 500;
/** On wide maps the margin grows with the map. */
const PAD_SHARE = 0.04;
/** World margin kept above/below; small, so a wide card doesn't zoom far out for one NPC. */
const VPAD = 200;

/** The narrowest window the card will show on a map `mapWidth` wide. */
export function minWindowWidth(mapWidth: number): number {
  return clamp(mapWidth * MIN_WINDOW_SHARE, MIN_WINDOW_WIDTH, MAX_MIN_WINDOW_WIDTH);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * The viewBox window that frames `points` inside a map of `mapWidth×mapHeight`
 * at the card's aspect. No points → the whole map. The window never leaves
 * the map; when the map is shorter than the window wants, the window narrows
 * to keep the aspect (the card letterboxes rather than distorting).
 */
export function frameGuidanceWindow(
  points: readonly WorldPoint[], mapWidth: number, mapHeight: number, aspect = GUIDANCE_CARD_ASPECT,
): ViewWindow {
  if (points.length === 0) return { x: 0, y: 0, width: mapWidth, height: mapHeight };
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const pad = Math.max(MIN_PAD, mapWidth * PAD_SHARE);
  let width = Math.max(minWindowWidth(mapWidth), maxX - minX + pad * 2, (maxY - minY + VPAD * 2) * aspect);
  width = Math.min(width, mapWidth);
  let height = width / aspect;
  if (height > mapHeight) {
    height = mapHeight;
    width = Math.min(mapWidth, height * aspect);
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return {
    x: Math.round(clamp(cx - width / 2, 0, mapWidth - width)),
    y: Math.round(clamp(cy - height / 2, 0, mapHeight - height)),
    width: Math.round(width),
    height: Math.round(height),
  };
}

/** Whether a world point falls inside the window (for the "you" dot). */
export function windowContains(win: ViewWindow, p: WorldPoint): boolean {
  return p.x >= win.x && p.x <= win.x + win.width && p.y >= win.y && p.y <= win.y + win.height;
}
